import { Component, inject } from '@angular/core';
import { injectUrlViewState } from '../../../utils/url-view-state.util';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { ConfirmationDialogComponent } from '../../controls/fnf-confirmation/confirmation-dialog/confirmation-dialog.component';
import { NgxSpinnerService } from 'ngx-spinner';
import { FNFResponse } from '../../../models/response';
import { StaffService } from '../../../services/staff.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UsPhonePipe } from '../../../pipes/us-phone.pipe';

@Component({
  selector: 'app-staffs',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, UsPhonePipe],
  templateUrl: './staffs.component.html',
  styleUrl: './staffs.component.css',
})
export class StaffsComponent {
  private router = inject(Router);
  staffService = inject(StaffService);
  ngxSpinnerService = inject(NgxSpinnerService);
  staffs1: any = [];
  displayStaffs: any = [];
  selectedType: any = 'active';
  viewMode: 'grid' | 'list' = 'list';
  /** Status tab and List / Grid live in the URL (?status, ?view). */
  private urlState = injectUrlViewState();
  searchTerm: string = '';

  readonly staffTabs = [
    { value: 'active', label: 'Active Staff' },
    { value: 'pending', label: 'Pending Staff' },
    { value: 'suspended', label: 'Suspended Staff' },
    { value: 'former', label: 'Former Staff' },
  ];

  /** Tab counts come from the full list, so they stay stable while searching. */
  tabCount(type: string): number {
    if (type === 'former') {
      return this.staffs.filter((f: any) => f.is_deleted).length;
    }
    return this.staffs.filter((f: any) => f.user_status === type && !f.is_deleted).length;
  }

  get visibleStaffs(): any[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.displayStaffs;
    }
    return this.displayStaffs.filter(
      (staff: any) =>
        staff.first_name?.toLowerCase().includes(term) ||
        staff.email?.toLowerCase().includes(term) ||
        this.roleLabel(staff.user_role).toLowerCase().includes(term),
    );
  }

  onSearch(event: any) {
    this.searchTerm = event.target.value;
  }

  roleLabel(role: string): string {
    if (role === 'restaurant_admin') {
      return 'Admin';
    }
    if (role === 'restaurant_manager') {
      return 'Manager';
    }
    if (role === 'restaurant_waiter') {
      return 'Waiter';
    }
    return 'General User';
  }

  /** Pill tone per role, matching the design. */
  roleTone(role: string): string {
    if (role === 'restaurant_admin') {
      return 'role-admin';
    }
    if (role === 'restaurant_manager') {
      return 'role-manager';
    }
    if (role === 'restaurant_waiter') {
      return 'role-waiter';
    }
    return 'role-general';
  }

  shiftLabel(shift: string): string {
    if (shift === 'full_time') {
      return 'Full-Time';
    }
    if (shift === 'part_time') {
      return 'Part-Time';
    }
    return 'Shift-Based';
  }

  shiftTone(shift: string): string {
    if (shift === 'full_time') {
      return 'shift-full';
    }
    if (shift === 'part_time') {
      return 'shift-part';
    }
    return 'shift-based';
  }
  staffs: any = [];
  displayLoading: boolean = false;

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
  ) {}

  ngOnInit() {
    this.selectedType = this.urlState.pick(
      'status',
      this.staffTabs.map((t) => t.value),
      'active',
    );
    this.viewMode = this.urlState.pick<'grid' | 'list'>('view', ['grid', 'list'], 'list');
    this.getStaffs();
  }

  /** List / Grid, kept in the URL (?view) so a browser reload keeps it. */
  setViewMode(mode: 'grid' | 'list') {
    this.viewMode = mode;
    this.urlState.set('view', mode, 'list');
  }

  getStaffs() {
    this.displayLoading = true;
    this.staffService.getAllStaffs().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.staffs = response.data;
        if (localStorage.getItem('user')) {
          let user: any = localStorage.getItem('user');
          if (user) {
            let jsonUser = JSON.parse(user);
            let findCurrentUser = this.staffs.find((f: any) => f.user_id == jsonUser.user_id);
            if (findCurrentUser) {
              findCurrentUser.isCurrentUser = true;
            }
          }
        }
        this.staffs.map((staff: any) => {
          staff.avatar = staff.avatar ? staff.avatar : './assets/default/profile.png';
        });
        this.filterStaffs(this.selectedType);
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  addNewStaff(staff?: any) {
    this.router.navigate(staff ? ['/edit-staff', staff.user_id] : ['/add-staff']);
  }

  saveNewStaff(staff: any) {
    if (localStorage.getItem('user')) {
      let user: any = localStorage.getItem('user');
      if (user) {
        let jsonUser = JSON.parse(user);
        staff.restaurant_id = jsonUser.restaurant_id;
      }
    }
    this.ngxSpinnerService.show();
    this.staffService.createNewStaff(staff).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.toastr.success('Staff added successfully', 'Success');
          this.getStaffs();
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  editStaff(staff: any, id: any) {
    if (localStorage.getItem('user')) {
      let user: any = localStorage.getItem('user');
      if (user) {
        let jsonUser = JSON.parse(user);
        staff.restaurant_id = jsonUser.restaurant_id;
      }
    }
    this.ngxSpinnerService.show();
    this.staffService.editStaff(staff, id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.toastr.success('Staff updated successfully', 'Success');
          this.getStaffs();
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  /** The signed-in admin can't suspend or remove themselves and get locked out (FNF-1532). */
  canSuspendOrRemove(staff: any): boolean {
    return !staff.isCurrentUser;
  }

  // Opens a confirmation dialog to delete a zone
  deleteStaffConfirm(staff: any) {
    if (!this.canSuspendOrRemove(staff)) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: 'This will permanently delete this staff and you can’t undo this later.',
        buttonText: {
          ok: 'Yes, Delete',
          cancel: 'No, Cancel',
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteStaff(staff);
      }
    });
  }

  suspendStaffConfirm(staff: any) {
    if (!this.canSuspendOrRemove(staff)) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: 'This will temporally suspend your current account.',
        buttonText: {
          ok: 'Yes, Suspend',
          cancel: 'No, Cancel',
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.suspendStaff(staff);
      }
    });
  }

  suspendStaff(staff: any) {
    this.ngxSpinnerService.show();
    this.staffService.suspendStaff(staff.user_id, staff).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          if (response.message) this.toastr.error(response.message, 'Error');
          else this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.ngxSpinnerService.hide();
        this.toastr.success('Staff suspended successfully', 'Success');
        this.getStaffs();
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  unSuspendStaffConfirm(staff: any) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: 'This will be activate your suspended account.',
        buttonText: {
          ok: 'Yes, Unsuspend',
          cancel: 'No, Cancel',
        },
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.unSuspendStaff(staff);
      }
    });
  }

  unSuspendStaff(staff: any) {
    this.ngxSpinnerService.show();
    this.staffService.unSuspendStaff(staff.user_id, staff).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          if (response.message) this.toastr.error(response.message, 'Error');
          else this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.ngxSpinnerService.hide();
        this.toastr.success('Staff unsuspended successfully', 'Success');
        this.getStaffs();
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  // Deletes a staff from the database
  deleteStaff(staff: any) {
    this.ngxSpinnerService.show();
    this.staffService.deleteStaff(staff.user_id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          return;
        } else if (!response.data) {
          if (response.message) this.toastr.error(response.message, 'Error');
          else this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          return;
        }
        this.ngxSpinnerService.hide();
        this.toastr.success('Staff deleted successfully', 'Success');
        this.getStaffs();
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  filterStaffs(type: any) {
    this.selectedType = type;
    this.urlState.set('status', type, 'active');
    if (type == 'active') {
      this.displayStaffs = this.staffs.filter(
        (f: any) => f.user_status == 'active' && !f.is_deleted,
      );
    } else if (type == 'pending') {
      this.displayStaffs = this.staffs.filter(
        (f: any) => f.user_status == 'pending' && !f.is_deleted,
      );
    } else if (type == 'former') {
      this.displayStaffs = this.staffs.filter((f: any) => f.is_deleted);
    } else if (type == 'suspended') {
      this.displayStaffs = this.staffs.filter(
        (f: any) => f.user_status == 'suspended' && !f.is_deleted,
      );
    }
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = './../../../../assets/default/profile.png';
  }

  setPinStaff(staff: any) {
    this.router.navigate(['/set-pin', staff.user_id]);
  }

  viewPinStaff(staff: any) {
    console.log(staff);
    this.displayLoading = true;
    this.staffService.viewPinStaff(staff.user_id).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        console.log(response.data);
        this.displayLoading = false;
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }
}
